/**
 * The DDI portfolio slide: UK&I Active Portfolio Overview, at PowerPoint 16:9.
 *
 * The portfolio-level read of the same monthly status reports that drive
 * ReportSlide. Initiatives are grouped under the portfolio leading them, and
 * every summary and way forward is shown in full: this is a slide, read on a
 * screen or in a deck, with nowhere to click through to the rest of a
 * sentence. When the text is long the type steps down until it fits rather
 * than anything being cut.
 *
 * Colours follow the Danone brand book: text in the primary blues, the
 * secondary palette only on status marks, and 10% tints only.
 *
 * Pure render: everything shown is computed by lib/ddi from reports already
 * filed, so any past month redraws exactly as it was signed off.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { DdiRow, DdiStatus } from '../lib/ddi'
import {
  ddiDeltas, ddiDirection, ddiGroups, ddiLayout, ddiMovement, ddiShare, formatBudget, formatGoLive, stageCounts,
} from '../lib/ddi'
import { shortMonthLabel } from '../lib/report'
import { PROJECT_STAGES } from '../types'
import { SLIDE_W, SLIDE_H } from './ReportSlide'

// Brand-book palette (pp.50-51, 56). BAR_GREEN is the accent bar's fixed
// Pantone 361 and appears nowhere else; every green that MEANS on track is
// Pantone 348, the same green as the rest of TrackIT.
const BLUE = '#005EB8'
const CYAN = '#00ACED'
const NAVY = '#002677'
const BAR_GREEN = '#54AD18'
const GREEN = '#00843D'
const ORANGE = '#F88806'
const RED = '#EF3340'
// Tints are limited to 60, 30 and 10% by the brand book.
const BLUE_10 = '#E6EFF8'
const BLUE_30 = '#B3CFEA'
const BLUE_60 = '#669ED4'
const BLACK_60 = '#666666'
const BLACK_30 = '#B3B3B3'
const BLACK_10 = '#E6E6E6'

const COND = "'Danone One Condensed', 'Danone One', sans-serif"

export const STATUS_HEX: Record<DdiStatus, string> = {
  on_track: GREEN,
  at_risk: ORANGE,
  roadblocks: RED,
  closed: BLUE,
}

export const STATUS_LABEL: Record<DdiStatus, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  roadblocks: 'Roadblocks',
  closed: 'Closed',
}

const STAGE_LABEL = Object.fromEntries(PROJECT_STAGES.map(s => [s.key, s.label]))

// Initiative, go live, prev/curr, summary, way forward, capex/opex. Summary
// takes the larger share of the prose width because it carries more words.
const GRID = '118px 40px 120px minmax(0, 1.5fr) minmax(0, 1fr) 98px'
const RAIL_W = 96

/**
 * Type sizes to try, largest first. The slide renders at the first step whose
 * body fits, so a short portfolio reads large and the twelve-row reference
 * portfolio lands at 9px summaries, 9.5px way forward.
 */
const TYPE_STEPS = [
  { name: 13, sum: 11, way: 11.5, pad: 6 },
  { name: 12.5, sum: 10.5, way: 11, pad: 5 },
  { name: 12, sum: 10, way: 10.5, pad: 4 },
  { name: 11.5, sum: 9.5, way: 10, pad: 3 },
  { name: 11, sum: 9, way: 9.5, pad: 2.5 },
  { name: 10.5, sum: 8.5, way: 9, pad: 2 },
  { name: 10, sum: 8, way: 8.5, pad: 2 },
] as const

const caps = (px: number, color: string, tracking: string) => ({
  fontFamily: COND,
  fontSize: px,
  fontWeight: 700,
  letterSpacing: tracking,
  textTransform: 'uppercase' as const,
  color,
})

/** Chevron shape shared with ReportSlide's stage ribbon. */
const chevron = (i: number, last: number) =>
  i === 0
    ? 'polygon(0 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 0 100%)'
    : i === last
      ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 9px 50%)'
      : 'polygon(0 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 0 100%, 9px 50%)'

/** The stage ribbon carrying counts rather than a current-stage marker: this
 *  slide is about the portfolio's spread across the gates, not one project's
 *  position in them. */
function CountRibbon({ counts }: { counts: Record<string, number> }) {
  return (
    <div style={{ display: 'flex', gap: 3, padding: 4 }}>
      {PROJECT_STAGES.map((s, i) => {
        const n = counts[s.key] ?? 0
        return (
          <div key={s.key}
               style={{
                 flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                 padding: '4px 10px', background: BLUE_10, clipPath: chevron(i, PROJECT_STAGES.length - 1),
               }}>
            <span style={caps(9, n > 0 ? BLUE : BLUE_60, '1.1px')}>{s.label}</span>
            <span style={{
              ...caps(9, n > 0 ? '#FFFFFF' : BLUE, '0'), background: n > 0 ? BLUE : BLUE_30,
              borderRadius: 8, padding: '1px 6px', lineHeight: 1.2,
            }}>{n}</span>
          </div>
        )
      })}
    </div>
  )
}

/** A small solid triangle: up for better, down for worse, a dash for no
 *  change. One rule for the header and every row. */
function Triangle({ tone, size = 9 }: { tone: 'better' | 'worse' | 'same'; size?: number }) {
  if (tone === 'same') {
    return <span style={{ display: 'block', width: 8, height: 2, borderRadius: 1, background: BLACK_30 }} />
  }
  const up = tone === 'better'
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" style={{ flexShrink: 0, display: 'block' }} aria-hidden="true">
      <path d={up ? 'M6 1.5 L11 10 H1 Z' : 'M6 10.5 L11 2 H1 Z'} fill={up ? GREEN : RED} />
    </svg>
  )
}

function StatusCell({ row }: { row: DdiRow }) {
  const dir = ddiDirection(row.previous, row.overall)
  const tone = dir === 'improved' ? 'better' : dir === 'worsened' ? 'worse' : 'same'
  const chip = row.status
    ? { bg: STATUS_HEX[row.status], fg: '#FFFFFF', label: row.stage ? STAGE_LABEL[row.stage] : STATUS_LABEL[row.status] }
    : { bg: BLACK_10, fg: BLACK_60, label: 'No report' }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
      {/* Previous month: grey when the project had not reported, so a first
          appearance reads as new rather than as healthy. */}
      <span style={{ display: 'block', flexShrink: 0, width: 20, height: 12, borderRadius: 3,
                     background: row.prevStatus ? STATUS_HEX[row.prevStatus] : BLACK_30 }} />
      <span style={{ display: 'flex', width: 9, justifyContent: 'center' }}
            title={tone === 'better' ? 'Improved since last month' : tone === 'worse' ? 'Worse than last month' : undefined}>
        <Triangle tone={tone} />
      </span>
      {/* Fixed width so the chips form a clean column; long stage names wrap. */}
      <span className="w-[84px]"
            title={row.status ? STATUS_LABEL[row.status] : undefined}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, minHeight: 18,
              padding: '2px 4px', borderRadius: 4, background: chip.bg, textAlign: 'center',
              ...caps(7.5, chip.fg, '0.5px'), lineHeight: 1.15,
            }}>
        {chip.label}
      </span>
    </div>
  )
}

/** Zero is a real figure inside a split, not "not yet a number". */
const money = (n: number) => (n === 0 ? '0' : formatBudget(n))

function BudgetCell({ capex, opex }: { capex: number; opex: number }) {
  const total = capex + opex
  if (total === 0) {
    return <div style={{ textAlign: 'right', ...caps(10, BLACK_60, '1px') }}>TBC</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: BLUE_10 }}>
        <div style={{ width: `${(capex / total) * 100}%`, background: BLUE }} />
        <div style={{ flex: '1 1 0', background: CYAN }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, fontWeight: 700, lineHeight: 1,
                    color: NAVY, fontVariantNumeric: 'tabular-nums' }}>
        <span>{money(capex)}</span><span>{money(opex)}</span>
      </div>
    </div>
  )
}

function Lines({ text }: { text: string }) {
  return <>{text.split('\n').filter(l => l.trim()).map((l, i) => <div key={i}>{l}</div>)}</>
}

function WayForward({ text, px }: { text: string; px: number }) {
  const lines = text.split('\n').filter(l => l.trim())
  if (lines.length === 0) return <div style={{ fontSize: px, color: BLACK_60 }}>-</div>
  return (
    <div style={{ fontSize: px, lineHeight: 1.3, color: NAVY, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <svg width="7" height="7" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: px * 0.33 }} aria-hidden="true">
            <polyline points="8 4 16 12 8 20" fill="none" stroke={CYAN} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ minWidth: 0 }}>{line}</span>
        </div>
      ))}
    </div>
  )
}

interface DdiSlideProps {
  rows: DdiRow[]
  month: string
  /** Portfolio names in Settings order, which the groups follow. */
  portfolioOrder?: string[]
  /** The portfolio this slide is narrowed to, named in its title. Absent on
   *  the full slide, which carries every portfolio. */
  scope?: string
  /** Told whether every word fits, even at the smallest type size. */
  onFitChange?: (fits: boolean) => void
  /** Makes each row a way into editing it. Absent on the exported slide. */
  onRowClick?: (projectId: string) => void
}

export default function DdiSlide({ rows, month, portfolioOrder = [], scope, onFitChange, onRowClick }: DdiSlideProps) {
  const layout = ddiLayout(rows.length)
  // Group first, then trim, so the rows kept are the ones at the top of the
  // grouped slide and no group loses a row from its middle.
  const shown = ddiGroups(rows, portfolioOrder).flatMap(g => g.rows).slice(0, layout.visible)
  const groups = ddiGroups(shown, portfolioOrder)
  const share = ddiShare(rows)
  const movement = ddiMovement(ddiDeltas(rows))
  const counts = stageCounts(rows)

  // The fitting: render at the largest type, step down while the body
  // overflows. Measured rather than estimated, because the column widths and
  // the font metrics do the deciding. Re-runs when the content changes and
  // once the brand font has loaded, since the fallback font measures
  // differently.
  const [fontsTick, setFontsTick] = useState(0)
  useEffect(() => {
    let live = true
    document.fonts?.ready?.then(() => { if (live) setFontsTick(t => t + 1) })
    return () => { live = false }
  }, [])
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const [step, setStep] = useState(0)
  const contentKey = `${month}:${fontsTick}:${portfolioOrder.join(',')}:` +
    shown.map(r => `${r.projectId}${r.portfolio}${r.summary.length}:${r.nextSteps.length}`).join('|')
  useLayoutEffect(() => { setStep(0) }, [contentKey])
  const fitRef = useRef<boolean | null>(null)
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const over = el.scrollHeight > el.clientHeight + 1
    if (over && step < TYPE_STEPS.length - 1) {
      setStep(s => s + 1)
      return
    }
    const fits = !over
    if (fitRef.current !== fits) {
      fitRef.current = fits
      onFitChange?.(fits)
    }
  })
  const t = TYPE_STEPS[step]

  return (
    <div className="relative bg-white overflow-hidden flex flex-col"
         style={{ width: SLIDE_W, height: SLIDE_H, border: '1px solid #000' }}>

      {/* Tri-colour accent bar - coloured stripes, as the brand book allows */}
      <div className="absolute top-0 left-0 right-0 h-[6px]"
           style={{ background: `linear-gradient(90deg, ${BLUE} 0%, ${BLUE} 40%, ${CYAN} 40%, ${CYAN} 70%, ${BAR_GREEN} 70%, ${BAR_GREEN} 100%)` }} />

      {/* Title and legend */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, padding: '16px 32px 7px', flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.4px', color: BLUE }}>
          UK&amp;I Active Portfolio Overview{' '}
          {scope && <span style={{ color: CYAN }}>{scope}{' '}</span>}
          <span style={{ display: 'inline-block', background: BLUE, color: '#FFFFFF', padding: '1px 9px 5px', borderRadius: 4, lineHeight: 1 }}>
            {shortMonthLabel(month)}
          </span>
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingBottom: 3 }}>
          {(['on_track', 'at_risk', 'roadblocks', 'closed'] as DdiStatus[]).map(s => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_HEX[s] }} />
              <span style={caps(9, NAVY, '1.4px')}>{STATUS_LABEL[s]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Header card: the stage ribbon and the portfolio's health in one frame */}
      <div style={{ margin: '0 32px 7px', border: `2px solid ${BLUE_10}`, borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
        <CountRibbon counts={counts} />
        <div style={{ display: 'flex', alignItems: 'stretch', borderTop: `1px solid ${BLUE_10}` }}>
          {/* Counts with the denominator stated. Figures stay in navy; the
              status colour sits on the dot beside each, as the brand book
              keeps text in the primary palette. */}
          <div style={{ flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, padding: '4px 0' }}>
            <span style={caps(9, BLACK_60, '1.5px')}>Of {share.active} active</span>
            {([
              ['On track', share.onTrack, GREEN, `(${share.onTrackPct}%)`],
              ['At risk', share.atRisk, ORANGE, ''],
              ['Roadblocks', share.roadblocks, RED, ''],
            ] as const).map(([label, n, color, pct]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
                <span style={caps(9.5, NAVY, '1px')}>{label}</span>
                <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, color: NAVY }}>{n}</span>
                {pct && <span style={{ fontSize: 10, fontWeight: 700, color: NAVY }}>{pct}</span>}
              </div>
            ))}
          </div>
          <div style={{ width: 1, background: BLUE_10 }} />
          {/* Movement as a sentence; the triangle carries the verdict. */}
          <div style={{ flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '4px 0' }}>
            <Triangle tone={movement.tone} size={10} />
            <span style={{ fontSize: 11, fontWeight: 700, color: NAVY }}>{movement.text}</span>
          </div>
        </div>
      </div>

      {/* The table: a solid blue band, then one navy rail per portfolio */}
      <div style={{ flex: '1 1 0', minHeight: 0, margin: '0 32px 12px', border: `2px solid ${BLUE_10}`, borderRadius: 12,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexShrink: 0, background: BLUE }}>
          <div style={{ width: RAIL_W, minWidth: RAIL_W, display: 'flex', alignItems: 'center', padding: '0 11px', ...caps(9, '#FFFFFF', '1.2px') }}>
            Portfolio
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0, display: 'grid', gridTemplateColumns: GRID, gap: 10, alignItems: 'center',
                        padding: '5px 12px', ...caps(9, '#FFFFFF', '1.2px') }}>
            <div>Initiative</div>
            <div style={{ textAlign: 'center' }}>Go live</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 4px 0 10px' }}><span>Prev</span><span>Curr</span></div>
            <div>Summary</div>
            <div>Way forward</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: BLUE, boxShadow: '0 0 0 1.5px #FFFFFF' }} />Capex
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Opex<span style={{ width: 8, height: 8, borderRadius: 2, background: CYAN, boxShadow: '0 0 0 1.5px #FFFFFF' }} />
              </span>
            </div>
          </div>
        </div>

        <div ref={bodyRef} style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {shown.length === 0 && (
            <div style={{ fontSize: 11, color: BLACK_60, fontStyle: 'italic', padding: '12px 12px 0' }}>
              No projects are flagged for the DDI portfolio.
            </div>
          )}
          {groups.map((g, gi) => {
            const total = g.capex + g.opex
            return (
              <div key={g.portfolio} data-portfolio={g.portfolio}
                   style={{ flex: `${g.rows.length} 0 auto`, display: 'flex', borderTop: gi === 0 ? undefined : `2px solid ${BLUE_10}` }}>
                <div style={{ width: RAIL_W, minWidth: RAIL_W, maxWidth: RAIL_W, boxSizing: 'border-box', display: 'flex',
                              flexDirection: 'column', justifyContent: 'center', gap: 3, padding: '0 11px', background: NAVY }}>
                  <span style={{ ...caps(10.5, '#FFFFFF', '1.1px'), lineHeight: 1.1 }}>{g.portfolio}</span>
                  <span style={{ fontSize: 9, color: CYAN }}>{g.rows.length} initiative{g.rows.length === 1 ? '' : 's'}</span>
                  {total > 0 && <span style={{ fontSize: 9, fontWeight: 700, color: '#FFFFFF' }}>{formatBudget(total)}</span>}
                </div>
                <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  {g.rows.map((row, i) => (
                    <div key={row.projectId} data-row={row.projectId}
                         onClick={onRowClick ? () => onRowClick(row.projectId) : undefined}
                         role={onRowClick ? 'button' : undefined}
                         tabIndex={onRowClick ? 0 : undefined}
                         onKeyDown={onRowClick ? e => { if (e.key === 'Enter') onRowClick(row.projectId) } : undefined}
                         title={onRowClick ? `Open ${row.name}` : undefined}
                         className={onRowClick ? 'ddi-slide-row' : undefined}
                         style={{
                           flex: '1 0 auto', display: 'grid', gridTemplateColumns: GRID, gap: 10, alignItems: 'center',
                           padding: `${t.pad}px 12px`,
                           borderBottom: i < g.rows.length - 1 ? `1px solid ${BLUE_10}` : undefined,
                           opacity: row.hasReport ? 1 : 0.55,
                           cursor: onRowClick ? 'pointer' : undefined,
                         }}>
                      <div style={{ fontSize: t.name, fontWeight: 700, lineHeight: 1.15, color: BLUE }}>{row.name}</div>
                      <div style={{ textAlign: 'center', fontSize: 10, color: NAVY, fontVariantNumeric: 'tabular-nums' }}>
                        {formatGoLive(row.goLive, row.goLivePrecision)}
                      </div>
                      <StatusCell row={row} />
                      <div style={{ fontSize: t.sum, lineHeight: 1.3, color: BLACK_60, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {!row.hasReport
                          ? <span style={{ fontStyle: 'italic' }}>No report yet</span>
                          : row.summary.trim()
                            ? <Lines text={row.summary} />
                            : <span style={{ fontStyle: 'italic' }}>No summary yet</span>}
                      </div>
                      <WayForward text={row.nextSteps} px={t.way} />
                      <BudgetCell capex={row.capex} opex={row.opex} />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
          {layout.hidden > 0 && (
            <div style={{ flexShrink: 0, fontSize: 9.5, color: BLACK_60, fontStyle: 'italic', padding: '5px 12px' }}>
              +{layout.hidden} not shown
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
